import { useCallback, useEffect, useMemo, useState } from "react";
import { Message } from "../components/Message";
import { useDataContext } from "../context/DataContext";
import { useViewPreferences } from "../context/ViewPreferencesContext";
import "./Styles/SchedulePage.css";
import { FLIGHT_COLOR, PERSONNEL_COLOR_PALETTE } from "./scheduleColors";

// Supervised person from API
interface SupervisedPerson {
  id: string;
  name: string;
}

// Note: Supervised data is now fetched from DataContext (supports both RC API and POC API Server)

interface ScheduleEvent {
  AssignmentScheduledEventId?: string | null;
  Abbr?: string | null;
  Colour?: string | null;
  Type?: string | null;
  Hours?: number | null;
  Date?: string | null;
}

interface AppDataAssignment {
  AssignmentId?: string | null;
  JobId?: string | null;
  Job?: {
    JobNumber?: number | string | null;
    Client?: { ClientName?: string | null } | null;
    Site?: { SiteId?: string | null; SiteName?: string | null } | null;
  } | null;
  Schedule?: ScheduleEvent[] | null;
}

interface CalendarScheduleEvent {
  date: Date;
  type: string;
  hours: number | null;
  personnelId: string;
  personnelName: string;
  jobNumber: string;
  clientName: string;
  siteName: string;
  color: string;
}

interface CalendarFlightEvent {
  date: Date;
  flightNumber: string;
  carrier: string;
  departTime: string;
  arriveTime: string | null;
  departAirport: string;
  arriveAirport: string;
}

type CalendarEvent = 
  | (CalendarScheduleEvent & { eventType: "schedule" })
  | (CalendarFlightEvent & { eventType: "flight" });

interface CalendarDay {
  date: number;
  month: number;
  year: number;
  inMonth: boolean;
  iso: string;
  events: CalendarEvent[];
}

type DetailFetchState = "idle" | "loading" | "success" | "error";

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const extractRows = (value: unknown): Record<string, unknown>[] => {
  // Direct array
  if (Array.isArray(value)) {
    return value.filter(isRecord);
  }
  if (isRecord(value)) {
    // Proxy wraps in { data: ... }
    const dataValue = value["data"];
    if (Array.isArray(dataValue)) {
      return dataValue.filter(isRecord);
    }
    // OData wraps in { value: [...] }
    if (isRecord(dataValue)) {
      const odataValue = dataValue["value"];
      if (Array.isArray(odataValue)) {
        return odataValue.filter(isRecord);
      }
    }
    // Direct OData response { value: [...] }
    const directOdata = value["value"];
    if (Array.isArray(directOdata)) {
      return directOdata.filter(isRecord);
    }
  }
  return [];
};

const toStringOrNull = (value: unknown): string | null => {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return null;
};

// Sort events with flights first
function sortEventsFlightsFirst(events: CalendarEvent[]): CalendarEvent[] {
  return [...events].sort((a, b) => {
    // Flights come first
    if (a.eventType === "flight" && b.eventType !== "flight") return -1;
    if (a.eventType !== "flight" && b.eventType === "flight") return 1;
    return 0;
  });
}

function buildCalendarDays(year: number, month: number, events: CalendarEvent[]): CalendarDay[] {
  const byIso = new Map<string, CalendarEvent[]>();
  events.forEach(event => {
    const iso = event.date.toISOString().slice(0, 10);
    const list = byIso.get(iso) ?? [];
    list.push(event);
    byIso.set(iso, list);
  });
  
  // Sort each day's events with flights first
  byIso.forEach((dayEvents, iso) => {
    byIso.set(iso, sortEventsFlightsFirst(dayEvents));
  });

  const firstOfMonth = new Date(Date.UTC(year, month, 1));
  // Convert Sunday=0 to Monday-first (Mon=0, Tue=1, ..., Sun=6)
  const dayOfWeek = firstOfMonth.getUTCDay();
  const startDayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const startDate = new Date(firstOfMonth);
  startDate.setUTCDate(firstOfMonth.getUTCDate() - startDayOffset);

  const days: CalendarDay[] = [];
  for (let i = 0; i < 42; i++) {
    const current = new Date(startDate);
    current.setUTCDate(startDate.getUTCDate() + i);
    const iso = current.toISOString().slice(0, 10);
    days.push({
      date: current.getUTCDate(),
      month: current.getUTCMonth(),
      year: current.getUTCFullYear(),
      inMonth: current.getUTCMonth() === month,
      iso,
      events: byIso.get(iso) ?? []
    });
  }
  return days;
}

// Helper to parse flight bookings
function parseFlightBookingsForCalendar(data: unknown): CalendarFlightEvent[] {
  const rows = extractRows(data);
  const events: CalendarFlightEvent[] = [];
  
  rows.forEach(booking => {
    const flightData = booking["Flight"];
    if (!isRecord(flightData)) return;
    
    const departAt = toStringOrNull(flightData["DepartAt"]);
    if (!departAt) return;
    
    const date = new Date(departAt);
    if (Number.isNaN(date.getTime())) return;
    
    const formatTime = (dateStr: string | null): string => {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleTimeString("en-AU", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "UTC"
      });
    };
    
    const getAirportCode = (airport: string | null): string => {
      if (!airport || airport.trim() === "") return "TBD";
      const a = airport.toLowerCase();
      if (a.includes("perth")) return "PER";
      if (a.includes("sydney")) return "SYD";
      if (a.includes("melbourne")) return "MEL";
      if (a.includes("brisbane")) return "BNE";
      if (a.includes("adelaide")) return "ADL";
      if (a.includes("darwin")) return "DRW";
      return airport.substring(0, 3).toUpperCase();
    };
    
    events.push({
      date,
      flightNumber: toStringOrNull(flightData["FlightNumber"]) ?? "—",
      carrier: toStringOrNull(flightData["Carrier"]) ?? "—",
      departTime: formatTime(departAt),
      arriveTime: formatTime(toStringOrNull(flightData["ArriveBy"])),
      departAirport: getAirportCode(toStringOrNull(flightData["DepartAirport"])),
      arriveAirport: getAirportCode(toStringOrNull(flightData["ArriveAirport"]))
    });
  });
  
  return events;
}

export default function SchedulePage(): JSX.Element {
  const { personnelAssignmentsMap, personnelNameMap, flightBookingsData, getPersonnelAssignments, currentPersonnelId, supervisedData, isSupervisor } = useDataContext();
  const { calendarPrefs, setCalendarViewModeDefault } = useViewPreferences();
  const { weekStart } = calendarPrefs;

  // Supervised people state
  const [supervisedPeople, setSupervisedPeople] = useState<SupervisedPerson[]>([]);
  const [supervisedLoading, setSupervisedLoading] = useState(false);
  const [selectedPersonnelIds, setSelectedPersonnelIds] = useState<string[]>([]);
  const [showPersonnelPicker, setShowPersonnelPicker] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  
  const [detailStatus, setDetailStatus] = useState<DetailFetchState>("idle");
  const [detailError, setDetailError] = useState<string | null>(null);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  
  // Parse flight bookings
  const flightEvents = useMemo(() => {
    if (!flightBookingsData) return [];
    return parseFlightBookingsForCalendar(flightBookingsData);
  }, [flightBookingsData]);

  // Parse supervised people from context data (supports both RC API and POC API Server)
  useEffect(() => {
    if (!currentPersonnelId || !supervisedData) {
      setSupervisedPeople([]);
      return;
    }

    setSupervisedLoading(true);
    try {
      const rows = extractRows(supervisedData);
      
      // Parse supervised people
      const people: SupervisedPerson[] = rows.map((person, idx) => {
        const id = toStringOrNull(person.Id) ?? toStringOrNull(person.PersonnelId) ?? `person-${idx}`;
        const given = (toStringOrNull(person.GivenNames) ?? toStringOrNull(person.GivenName) ?? "").trim();
        const surname = (toStringOrNull(person.Surname) ?? toStringOrNull(person.LastName) ?? "").trim();
        const name = [given, surname].filter(Boolean).join(" ").trim() || `Person ${idx + 1}`;
        return { id, name };
      });
      
      // Sort alphabetically by name
      people.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
      
      setSupervisedPeople(people);
    } catch (err) {
      console.warn("Failed to parse supervised data:", err);
      setSupervisedPeople([]);
    } finally {
      setSupervisedLoading(false);
    }
  }, [currentPersonnelId, supervisedData]);

  // Initialize selectedPersonnelIds to just the current person when it changes
  useEffect(() => {
    if (currentPersonnelId && !hasInitialized) {
      setSelectedPersonnelIds([currentPersonnelId]);
      setHasInitialized(true);
    }
  }, [currentPersonnelId, hasInitialized]);

  // Fetch assignments data for selected personnel who aren't already cached
  useEffect(() => {
    const fetchMissingAssignments = async () => {
      for (const personnelId of selectedPersonnelIds) {
        if (!personnelAssignmentsMap.has(personnelId)) {
          await getPersonnelAssignments(personnelId);
        }
      }
    };
    
    if (selectedPersonnelIds.length > 0) {
      void fetchMissingAssignments();
    }
  }, [selectedPersonnelIds, personnelAssignmentsMap, getPersonnelAssignments]);

  const buildCalendarEventsFromCache = useCallback((personnelIds: string[]) => {
    if (personnelIds.length === 0 && flightEvents.length === 0) {
      setDetailStatus("idle");
      setCalendarEvents([]);
      return;
    }

    setDetailStatus("loading");
    setDetailError(null);

    const allEvents: CalendarEvent[] = [];
    const colorMap = new Map<string, string>();
    let colorIndex = 0;

    // Add schedule events
    for (const personnelId of personnelIds) {
      const personnelName = personnelNameMap.get(personnelId) ?? "Unknown";

      // Assign color per personnel
      if (!colorMap.has(personnelId)) {
        colorMap.set(personnelId, PERSONNEL_COLOR_PALETTE[colorIndex % PERSONNEL_COLOR_PALETTE.length]);
        colorIndex++;
      }
      const color = colorMap.get(personnelId)!;

      // Use cached data from context
      const personnelData = personnelAssignmentsMap.get(personnelId);
      if (!personnelData) continue;

      personnelData.assignments.forEach(raw => {
        const assignment = raw as AppDataAssignment;
        const jobNumber = String(assignment.Job?.JobNumber ?? "—");
        const clientName = assignment.Job?.Client?.ClientName ?? "—";
        const siteName = assignment.Job?.Site?.SiteName ?? "—";
        const scheduleEvents = Array.isArray(assignment.Schedule) ? assignment.Schedule : [];

        scheduleEvents.forEach(evt => {
          const dateValue = evt.Date;
          if (!dateValue) return;
          const date = new Date(dateValue);
          if (Number.isNaN(date.getTime())) return;

          allEvents.push({
            eventType: "schedule",
            date,
            type: evt.Type ?? "—",
            hours: evt.Hours ?? null,
            personnelId,
            personnelName,
            jobNumber,
            clientName,
            siteName,
            color
          });
        });
      });
    }

    // Add flight events
    flightEvents.forEach(flight => {
      allEvents.push({
        eventType: "flight",
        ...flight
      });
    });

    allEvents.sort((a, b) => a.date.getTime() - b.date.getTime());

    setCalendarEvents(allEvents);
    setDetailStatus("success");
  }, [personnelAssignmentsMap, personnelNameMap, flightEvents]);

  useEffect(() => {
    if (selectedPersonnelIds.length === 0 && flightEvents.length === 0) {
      setDetailStatus("idle");
      setCalendarEvents([]);
      return;
    }
    buildCalendarEventsFromCache(selectedPersonnelIds);
  }, [selectedPersonnelIds, flightEvents, buildCalendarEventsFromCache]);

  // Choose Agenda or Calendar once, from the week on screen when the events arrive.
  // Week and month navigation also updates weekStart; that must not run this again,
  // or a week with no events flips the view to Calendar and the next week flips it back.
  useEffect(() => {
    if (detailStatus !== "success") return;

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    const weekStartTime = weekStart.getTime();
    const weekEndTime = weekEnd.getTime();

    const hasEventsThisWeek = calendarEvents.some(event => {
      const eventTime = event.date.getTime();
      return eventTime >= weekStartTime && eventTime <= weekEndTime;
    });

    setCalendarViewModeDefault(hasEventsThisWeek ? "agenda" : "month");
    // weekStart is read for that first choice. Leaving it out of the deps keeps later navigation from changing the view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailStatus, calendarEvents, setCalendarViewModeDefault]);

  const handleTogglePerson = useCallback((personId: string) => {
    setSelectedPersonnelIds(prev => {
      if (prev.includes(personId)) {
        // Don't allow deselecting if it's the last one
        if (prev.length === 1) return prev;
        return prev.filter(id => id !== personId);
      }
      return [...prev, personId];
    });
  }, []);

  // Build display text for personnel picker button
  const selectedPersonnelLabel = useMemo(() => {
    if (selectedPersonnelIds.length === 0) return "Select People";
    if (selectedPersonnelIds.length === 1) {
      const person = supervisedPeople.find(p => p.id === selectedPersonnelIds[0]);
      // Also check if it's the current person (might not be in supervised list)
      if (person) return person.name;
      const nameFromMap = personnelNameMap.get(selectedPersonnelIds[0]);
      if (nameFromMap) return nameFromMap;
      return "1 person";
    }
    return `${selectedPersonnelIds.length} people`;
  }, [selectedPersonnelIds, supervisedPeople, personnelNameMap]);

  return (
    <div className="calendar-page">
      {detailStatus === "loading" && <Message type="info" text="Loading schedules..." />}
      {detailStatus === "error" && (
        <Message type="error" text={detailError ?? "Failed to load schedule."} />
      )}
      {(detailStatus === "success" || detailStatus === "idle") && (
        <PersonnelCalendar 
          events={calendarEvents}
          selectedPersonnelLabel={selectedPersonnelLabel}
          onOpenPersonnelPicker={() => setShowPersonnelPicker(true)}
          showPeoplePicker={isSupervisor}
        />
      )}

      {/* Personnel Picker Modal - only shown in supervisor mode */}
      {isSupervisor && showPersonnelPicker && (
        <PersonnelPickerModal
          currentPersonnelId={currentPersonnelId}
          supervisedPeople={supervisedPeople}
          selectedPersonnelIds={selectedPersonnelIds}
          onToggle={handleTogglePerson}
          onClear={() => currentPersonnelId && setSelectedPersonnelIds([currentPersonnelId])}
          onClose={() => setShowPersonnelPicker(false)}
          isLoading={supervisedLoading}
          personnelNameMap={personnelNameMap}
        />
      )}
    </div>
  );
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

// Helper to get Monday of the week for a given date
function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

// Build 7 days for a week starting from weekStart
function buildWeekDays(weekStart: Date, events: CalendarEvent[]): CalendarDay[] {
  const byIso = new Map<string, CalendarEvent[]>();
  events.forEach(event => {
    const iso = event.date.toISOString().slice(0, 10);
    const list = byIso.get(iso) ?? [];
    list.push(event);
    byIso.set(iso, sortEventsFlightsFirst(list));
  });

  const days: CalendarDay[] = [];
  for (let i = 0; i < 7; i++) {
    const current = new Date(weekStart);
    current.setDate(weekStart.getDate() + i);
    const iso = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
    days.push({
      date: current.getDate(),
      month: current.getMonth(),
      year: current.getFullYear(),
      inMonth: true,
      iso,
      events: byIso.get(iso) ?? []
    });
  }
  return days;
}

// Format week range for display
function formatWeekRange(weekStart: Date): string {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  
  const startMonth = weekStart.toLocaleString("default", { month: "short" });
  const endMonth = weekEnd.toLocaleString("default", { month: "short" });
  
  if (weekStart.getMonth() === weekEnd.getMonth()) {
    return `${startMonth} ${weekStart.getDate()} - ${weekEnd.getDate()}, ${weekStart.getFullYear()}`;
  } else if (weekStart.getFullYear() === weekEnd.getFullYear()) {
    return `${startMonth} ${weekStart.getDate()} - ${endMonth} ${weekEnd.getDate()}, ${weekStart.getFullYear()}`;
  } else {
    return `${startMonth} ${weekStart.getDate()}, ${weekStart.getFullYear()} - ${endMonth} ${weekEnd.getDate()}, ${weekEnd.getFullYear()}`;
  }
}

function PersonnelCalendar({
  events,
  selectedPersonnelLabel,
  onOpenPersonnelPicker,
  showPeoplePicker = true
}: {
  events: CalendarEvent[];
  selectedPersonnelLabel: string;
  onOpenPersonnelPicker: () => void;
  showPeoplePicker?: boolean;
}): JSX.Element {
  const { 
    calendarPrefs, 
    setCalendarViewMode, 
    setCalendarMonthYear, 
    setCalendarWeekStart 
  } = useViewPreferences();
  
  const { viewMode, year, month, weekStart } = calendarPrefs;
  
  const [selectedDay, setSelectedDay] = useState<CalendarDay | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showWeekPicker, setShowWeekPicker] = useState(false);
  
  const sortedEvents = [...events].sort((a, b) => a.date.getTime() - b.date.getTime());
  const monthDate = new Date(Date.UTC(year, month, 1));
  const monthName = monthDate.toLocaleString("default", { month: "long", timeZone: "UTC" });
  const days = buildCalendarDays(year, month, sortedEvents);
  const weekDays = buildWeekDays(weekStart, sortedEvents);
  
  // Use local date for "today" highlight - format as YYYY-MM-DD in local timezone
  const now = new Date();
  const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const handleDayClick = (day: CalendarDay) => {
    if (day.events.length > 0) {
      setSelectedDay(day);
    }
  };

  const closeModal = () => {
    setSelectedDay(null);
  };

  const handleMonthChange = (offset: number) => {
    const newDate = new Date(Date.UTC(year, month + offset, 1));
    setCalendarMonthYear(newDate.getUTCFullYear(), newDate.getUTCMonth());
    // Sync agenda to first week of the new month
    const firstOfMonth = new Date(newDate.getUTCFullYear(), newDate.getUTCMonth(), 1);
    setCalendarWeekStart(getWeekStart(firstOfMonth));
  };

  const handleDatePickerSelect = (newYear: number, newMonth: number) => {
    setCalendarMonthYear(newYear, newMonth);
    setShowDatePicker(false);
    // Sync agenda to first week of the selected month
    const firstOfMonth = new Date(newYear, newMonth, 1);
    setCalendarWeekStart(getWeekStart(firstOfMonth));
  };

  const handleWeekChange = (offset: number) => {
    const newDate = new Date(weekStart);
    newDate.setDate(weekStart.getDate() + (offset * 7));
    setCalendarWeekStart(newDate);
    // Sync calendar to the month containing the first day of the new week
    setCalendarMonthYear(newDate.getFullYear(), newDate.getMonth());
  };

  const handleWeekPickerSelect = (date: Date) => {
    const newWeekStart = getWeekStart(date);
    setCalendarWeekStart(newWeekStart);
    setShowWeekPicker(false);
    // Sync calendar to the month containing the first day of the selected week
    setCalendarMonthYear(newWeekStart.getFullYear(), newWeekStart.getMonth());
  };

  return (
    <div className="personnel-calendar">
      <div className="personnel-calendar__header">
        {showPeoplePicker && (
          <button
            type="button"
            className="personnel-calendar__people-btn"
            onClick={onOpenPersonnelPicker}
            aria-label="Select people"
          >
            <svg
              className="personnel-calendar__people-icon"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="9" cy="8" r="3.5" />
              <path d="M2.5 20v-1a5 5 0 0 1 5-5h3a5 5 0 0 1 5 5v1" />
              <path d="M16 5.5a3.5 3.5 0 0 1 0 6.5" />
              <path d="M17.5 14.2A5 5 0 0 1 21.5 19v1" />
            </svg>
            <span className="personnel-calendar__people-label">{selectedPersonnelLabel}</span>
          </button>
        )}
        <div className="personnel-calendar__nav">
          {viewMode === "month" ? (
            <>
              <button
                type="button"
                className="personnel-calendar__nav-btn"
                aria-label="Previous month"
                onClick={() => handleMonthChange(-1)}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m15 6-6 6 6 6" />
                </svg>
              </button>
              <button
                type="button"
                className="personnel-calendar__title personnel-calendar__title--clickable"
                onClick={() => setShowDatePicker(true)}
                aria-label="Select month and year"
              >
                {`${monthName} ${year}`}
              </button>
              <button
                type="button"
                className="personnel-calendar__nav-btn"
                aria-label="Next month"
                onClick={() => handleMonthChange(1)}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m9 6 6 6-6 6" />
                </svg>
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="personnel-calendar__nav-btn"
                aria-label="Previous week"
                onClick={() => handleWeekChange(-1)}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m15 6-6 6 6 6" />
                </svg>
              </button>
              <button
                type="button"
                className="personnel-calendar__title personnel-calendar__title--clickable"
                onClick={() => setShowWeekPicker(true)}
                aria-label="Select week"
              >
                {formatWeekRange(weekStart)}
              </button>
              <button
                type="button"
                className="personnel-calendar__nav-btn"
                aria-label="Next week"
                onClick={() => handleWeekChange(1)}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m9 6 6 6-6 6" />
                </svg>
              </button>
            </>
          )}
        </div>
        <div className="personnel-calendar__view-toggle">
          <button
            type="button"
            className={`personnel-calendar__view-btn${viewMode === "agenda" ? " personnel-calendar__view-btn--active" : ""}`}
            onClick={() => setCalendarViewMode("agenda")}
          >
            Agenda
          </button>
          <button
            type="button"
            className={`personnel-calendar__view-btn${viewMode === "month" ? " personnel-calendar__view-btn--active" : ""}`}
            onClick={() => setCalendarViewMode("month")}
          >
            Calendar
          </button>
        </div>
      </div>
      <div className="personnel-calendar__body">
        {viewMode === "month" && (
          <>
            <div className="personnel-calendar__weekdays">
              {WEEKDAY_LABELS.map(label => (
                <div key={label} className="personnel-calendar__weekday">
                  {label}
                </div>
              ))}
            </div>
            <div className="personnel-calendar__grid">
              {days.map((day, idx) => {
                const isToday = day.iso === todayIso;
                const dayOfWeek = idx % 7; // 0 = Monday, 6 = Sunday
                const isWeekend = dayOfWeek === 5 || dayOfWeek === 6; // Saturday or Sunday
                const hasEvents = day.events.length > 0;
                return (
                  <div
                    key={day.iso}
                    className={`personnel-calendar__cell${day.inMonth ? "" : " personnel-calendar__cell--muted"}${isWeekend && !isToday ? " personnel-calendar__cell--weekend" : ""}${isToday ? " personnel-calendar__cell--today" : ""}${hasEvents ? " personnel-calendar__cell--clickable" : ""}`}
                    onClick={() => handleDayClick(day)}
                    role={hasEvents ? "button" : undefined}
                    tabIndex={hasEvents ? 0 : undefined}
                    onKeyDown={hasEvents ? (e) => { if (e.key === "Enter" || e.key === " ") handleDayClick(day); } : undefined}
                  >
                    <div className="personnel-calendar__date">
                      {day.date}
                    </div>
                    <div className="personnel-calendar__events">
                      {day.events.slice(0, 4).map((event, idx) => {
                        if (event.eventType === "flight") {
                          return (
                            <div
                              key={`flight-${event.flightNumber}-${idx}`}
                              className="personnel-calendar__event personnel-calendar__event--flight"
                              style={{ backgroundColor: FLIGHT_COLOR }}
                              title={`${event.carrier} ${event.flightNumber} - ${event.departAirport} → ${event.arriveAirport} @ ${event.departTime}`}
                            >
                              <span className="personnel-calendar__event-name">✈ {event.flightNumber}</span>
                              <span className="personnel-calendar__event-hours">{event.departTime}</span>
                            </div>
                          );
                        }
                        return (
                          <div
                            key={`${event.personnelId}-${idx}`}
                            className="personnel-calendar__event"
                            style={{ backgroundColor: event.color }}
                            title={`${event.personnelName} - ${event.type} (${event.hours ?? 0}h) - ${event.jobNumber}`}
                          >
                            <span className="personnel-calendar__event-name">{event.personnelName}</span>
                            <span className="personnel-calendar__event-type">{event.type}</span>
                          </div>
                        );
                      })}
                      {day.events.length > 4 && (
                        <div className="personnel-calendar__more">+{day.events.length - 4}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {viewMode === "agenda" && (
          <AgendaView days={weekDays} todayIso={todayIso} />
        )}
      </div>

      {/* Day Detail Modal */}
      {selectedDay && (
        <DayDetailModal day={selectedDay} onClose={closeModal} />
      )}

      {/* Month/Year Picker Modal */}
      {showDatePicker && (
        <MonthYearPickerModal
          currentYear={year}
          currentMonth={month}
          onSelect={handleDatePickerSelect}
          onClose={() => setShowDatePicker(false)}
        />
      )}

      {/* Week Picker Modal */}
      {showWeekPicker && (
        <WeekPickerModal
          currentWeekStart={weekStart}
          onSelect={handleWeekPickerSelect}
          onClose={() => setShowWeekPicker(false)}
        />
      )}
    </div>
  );
}

function AgendaView({
  days,
  todayIso
}: {
  days: CalendarDay[];
  todayIso: string;
}): JSX.Element {
  // Filter to only days with events
  const daysWithEvents = days.filter(day => day.events.length > 0);
  
  if (daysWithEvents.length === 0) {
    return (
      <div className="agenda-view">
        <div className="agenda-view__empty">No events this week</div>
      </div>
    );
  }

  return (
    <div className="agenda-view">
      {daysWithEvents.map(day => {
        const dateObj = new Date(Date.UTC(day.year, day.month, day.date));
        const isToday = day.iso === todayIso;
        const formattedDate = dateObj.toLocaleDateString("en-AU", {
          weekday: "long",
          day: "numeric",
          month: "short",
          timeZone: "UTC"
        });

        return (
          <div key={day.iso} className={`agenda-view__day${isToday ? " agenda-view__day--today" : ""}`}>
            <div className="agenda-view__date-header">
              <span className="agenda-view__date-text">{formattedDate}</span>
              {isToday && <span className="agenda-view__today-badge">Today</span>}
              <span className="agenda-view__event-count">{day.events.length} event{day.events.length !== 1 ? "s" : ""}</span>
            </div>
            <div className="agenda-view__events">
              {day.events.map((event, idx) => {
                if (event.eventType === "flight") {
                  return (
                    <div key={`flight-${event.flightNumber}-${idx}`} className="agenda-view__event agenda-view__event--flight">
                      <div className="agenda-view__event-indicator" style={{ backgroundColor: FLIGHT_COLOR }} />
                      <div className="agenda-view__event-content">
                        <div className="agenda-view__event-title">
                          ✈️ {event.carrier} {event.flightNumber}
                        </div>
                        <div className="agenda-view__event-details">
                          {event.departAirport} → {event.arriveAirport} • Departs {event.departTime}
                          {event.arriveTime && event.arriveTime !== "—" && ` • Arrives ${event.arriveTime}`}
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={`${event.personnelId}-${idx}`} className="agenda-view__event">
                    <div className="agenda-view__event-indicator" style={{ backgroundColor: event.color }} />
                    <div className="agenda-view__event-content">
                      <div className="agenda-view__event-title">{event.personnelName}</div>
                      <div className="agenda-view__event-details">
                        {event.type} • {event.hours ?? 0}h • Job #{event.jobNumber}
                      </div>
                      <div className="agenda-view__event-location">
                        {event.clientName} • {event.siteName}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MonthYearPickerModal({
  currentYear,
  currentMonth,
  onSelect,
  onClose
}: {
  currentYear: number;
  currentMonth: number;
  onSelect: (year: number, month: number) => void;
  onClose: () => void;
}): JSX.Element {
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handleGoToday = () => {
    const now = new Date();
    onSelect(now.getFullYear(), now.getMonth());
  };

  return (
    <div className="day-modal-overlay" onClick={onClose}>
      <div className="month-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="month-picker-modal__header">
          <h3 className="month-picker-modal__title">Select Month & Year</h3>
          <button
            type="button"
            className="day-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="month-picker-modal__content">
          <div className="month-picker-modal__year-nav">
            <button
              type="button"
              className="month-picker-modal__year-btn"
              onClick={() => setSelectedYear(y => y - 1)}
              aria-label="Previous year"
            >
              ‹
            </button>
            <span className="month-picker-modal__year-display">{selectedYear}</span>
            <button
              type="button"
              className="month-picker-modal__year-btn"
              onClick={() => setSelectedYear(y => y + 1)}
              aria-label="Next year"
            >
              ›
            </button>
          </div>
          <div className="month-picker-modal__months">
            {MONTH_NAMES.map((name, idx) => (
              <button
                key={name}
                type="button"
                className={`month-picker-modal__month${selectedMonth === idx ? " month-picker-modal__month--selected" : ""}`}
                onClick={() => setSelectedMonth(idx)}
              >
                {name.slice(0, 3)}
              </button>
            ))}
          </div>
          <div className="month-picker-modal__actions">
            <button
              type="button"
              className="month-picker-modal__today-btn"
              onClick={handleGoToday}
            >
              Today
            </button>
            <button
              type="button"
              className="month-picker-modal__go-btn"
              onClick={() => onSelect(selectedYear, selectedMonth)}
            >
              Go
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function WeekPickerModal({
  currentWeekStart,
  onSelect,
  onClose
}: {
  currentWeekStart: Date;
  onSelect: (date: Date) => void;
  onClose: () => void;
}): JSX.Element {
  const [selectedYear, setSelectedYear] = useState(currentWeekStart.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentWeekStart.getMonth());
  const [selectedWeekStart, setSelectedWeekStart] = useState<Date | null>(currentWeekStart);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Get weeks for the selected month
  const weeksInMonth = useMemo(() => {
    const weeks: { start: Date; end: Date; label: string }[] = [];
    const firstOfMonth = new Date(selectedYear, selectedMonth, 1);
    const lastOfMonth = new Date(selectedYear, selectedMonth + 1, 0);
    
    // Find the first Monday on or before the first of the month
    let current = getWeekStart(firstOfMonth);
    
    while (current <= lastOfMonth) {
      const weekEnd = new Date(current);
      weekEnd.setDate(current.getDate() + 6);
      
      const startDay = current.getDate();
      const endDay = weekEnd.getDate();
      const startMonth = current.toLocaleString("default", { month: "short" });
      const endMonth = weekEnd.toLocaleString("default", { month: "short" });
      
      let label: string;
      if (current.getMonth() === weekEnd.getMonth()) {
        label = `${startMonth} ${startDay} - ${endDay}`;
      } else {
        label = `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
      }
      
      weeks.push({ start: new Date(current), end: weekEnd, label });
      current.setDate(current.getDate() + 7);
    }
    
    return weeks;
  }, [selectedYear, selectedMonth]);

  const handleGoThisWeek = () => {
    const today = new Date();
    const thisWeekStart = getWeekStart(today);
    onSelect(thisWeekStart);
  };

  const isWeekSelected = (weekStart: Date) => {
    if (!selectedWeekStart) return false;
    return weekStart.getTime() === selectedWeekStart.getTime();
  };

  return (
    <div className="day-modal-overlay" onClick={onClose}>
      <div className="week-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="week-picker-modal__header">
          <h3 className="week-picker-modal__title">Select Week</h3>
          <button
            type="button"
            className="day-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="week-picker-modal__content">
          <div className="week-picker-modal__year-nav">
            <button
              type="button"
              className="week-picker-modal__nav-btn"
              onClick={() => setSelectedYear(y => y - 1)}
              aria-label="Previous year"
            >
              ‹
            </button>
            <span className="week-picker-modal__year-display">{selectedYear}</span>
            <button
              type="button"
              className="week-picker-modal__nav-btn"
              onClick={() => setSelectedYear(y => y + 1)}
              aria-label="Next year"
            >
              ›
            </button>
          </div>
          <div className="week-picker-modal__month-nav">
            <button
              type="button"
              className="week-picker-modal__nav-btn"
              onClick={() => {
                if (selectedMonth === 0) {
                  setSelectedMonth(11);
                  setSelectedYear(y => y - 1);
                } else {
                  setSelectedMonth(m => m - 1);
                }
              }}
              aria-label="Previous month"
            >
              ‹
            </button>
            <span className="week-picker-modal__month-display">
              {MONTH_NAMES[selectedMonth]}
            </span>
            <button
              type="button"
              className="week-picker-modal__nav-btn"
              onClick={() => {
                if (selectedMonth === 11) {
                  setSelectedMonth(0);
                  setSelectedYear(y => y + 1);
                } else {
                  setSelectedMonth(m => m + 1);
                }
              }}
              aria-label="Next month"
            >
              ›
            </button>
          </div>
          <div className="week-picker-modal__weeks">
            {weeksInMonth.map((week, idx) => (
              <button
                key={idx}
                type="button"
                className={`week-picker-modal__week${isWeekSelected(week.start) ? " week-picker-modal__week--selected" : ""}`}
                onClick={() => setSelectedWeekStart(week.start)}
              >
                {week.label}
              </button>
            ))}
          </div>
          <div className="week-picker-modal__actions">
            <button
              type="button"
              className="week-picker-modal__today-btn"
              onClick={handleGoThisWeek}
            >
              This Week
            </button>
            <button
              type="button"
              className="week-picker-modal__go-btn"
              onClick={() => selectedWeekStart && onSelect(selectedWeekStart)}
              disabled={!selectedWeekStart}
            >
              Go
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DayDetailModal({
  day,
  onClose
}: {
  day: CalendarDay;
  onClose: () => void;
}): JSX.Element {
  const dateObj = new Date(Date.UTC(day.year, day.month, day.date));
  const formattedDate = dateObj.toLocaleDateString("default", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC"
  });

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div className="day-modal-overlay" onClick={onClose}>
      <div className="day-modal" onClick={(e) => e.stopPropagation()}>
        <div className="day-modal__header">
          <h3 className="day-modal__title">{formattedDate}</h3>
          <button
            type="button"
            className="day-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="day-modal__content">
          <div className="day-modal__event-count">
            {day.events.length} event{day.events.length !== 1 ? "s" : ""}
          </div>
          <ul className="day-modal__list">
            {day.events.map((event, idx) => {
              if (event.eventType === "flight") {
                return (
                  <li key={`flight-${event.flightNumber}-${idx}`} className="day-modal__item day-modal__item--flight">
                    <div
                      className="day-modal__color-indicator"
                      style={{ backgroundColor: FLIGHT_COLOR }}
                    />
                    <div className="day-modal__item-content">
                      <div className="day-modal__item-name">
                        ✈️ {event.carrier} {event.flightNumber}
                      </div>
                      <div className="day-modal__item-details">
                        <span className="day-modal__item-route">
                          {event.departAirport} → {event.arriveAirport}
                        </span>
                      </div>
                      <div className="day-modal__item-job">
                        Departs {event.departTime}{event.arriveTime && event.arriveTime !== "—" ? ` • Arrives ${event.arriveTime}` : ""}
                      </div>
                    </div>
                  </li>
                );
              }
              return (
                <li key={`${event.personnelId}-${idx}`} className="day-modal__item">
                  <div
                    className="day-modal__color-indicator"
                    style={{ backgroundColor: event.color }}
                  />
                  <div className="day-modal__item-content">
                    <div className="day-modal__item-name">{event.personnelName}</div>
                    <div className="day-modal__item-details">
                      <span className="day-modal__item-type">{event.type}</span>
                      <span className="day-modal__item-hours">{event.hours ?? 0}h</span>
                    </div>
                    <div className="day-modal__item-job">
                      Job #{event.jobNumber} • {event.clientName} • {event.siteName}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

function PersonnelPickerModal({
  currentPersonnelId,
  supervisedPeople,
  selectedPersonnelIds,
  onToggle,
  onClear,
  onClose,
  isLoading,
  personnelNameMap
}: {
  currentPersonnelId: string | null;
  supervisedPeople: SupervisedPerson[];
  selectedPersonnelIds: string[];
  onToggle: (personId: string) => void;
  onClear: () => void;
  onClose: () => void;
  isLoading: boolean;
  personnelNameMap: Map<string, string>;
}): JSX.Element {
  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Build complete list: current person + supervised people (avoiding duplicates)
  const allPeople = useMemo(() => {
    const people: SupervisedPerson[] = [];
    
    // Add current person first if they exist
    if (currentPersonnelId) {
      const currentName = personnelNameMap.get(currentPersonnelId) ?? 
        supervisedPeople.find(p => p.id === currentPersonnelId)?.name ?? 
        "You";
      people.push({ id: currentPersonnelId, name: currentName });
    }
    
    // Add supervised people (excluding current person to avoid duplicates)
    supervisedPeople.forEach(person => {
      if (person.id !== currentPersonnelId) {
        people.push(person);
      }
    });
    
    return people;
  }, [currentPersonnelId, supervisedPeople, personnelNameMap]);

  return (
    <div className="day-modal-overlay" onClick={onClose}>
      <div className="personnel-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="personnel-picker-modal__header">
          <h3 className="personnel-picker-modal__title">Select People</h3>
          <button
            type="button"
            className="day-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="personnel-picker-modal__content">
          {isLoading ? (
            <div className="personnel-picker-modal__loading">Loading...</div>
          ) : allPeople.length === 0 ? (
            <div className="personnel-picker-modal__empty">No people available</div>
          ) : (
            <ul className="personnel-picker-modal__list">
              {allPeople.map(person => {
                const isSelected = selectedPersonnelIds.includes(person.id);
                const isCurrentPerson = person.id === currentPersonnelId;
                return (
                  <li key={person.id} className="personnel-picker-modal__item">
                    <button
                      type="button"
                      className={`personnel-picker-modal__person${isSelected ? " personnel-picker-modal__person--selected" : ""}`}
                      onClick={() => onToggle(person.id)}
                    >
                      <span className="personnel-picker-modal__checkbox">
                        {isSelected ? "✓" : ""}
                      </span>
                      <span className="personnel-picker-modal__name">
                        {person.name}
                        {isCurrentPerson && <span className="personnel-picker-modal__you-badge">(You)</span>}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="personnel-picker-modal__footer">
          <span className="personnel-picker-modal__count">
            {selectedPersonnelIds.length} selected
          </span>
          <div className="personnel-picker-modal__actions">
            <button
              type="button"
              className="personnel-picker-modal__clear-btn"
              onClick={onClear}
              disabled={selectedPersonnelIds.length === 1 && selectedPersonnelIds[0] === currentPersonnelId}
            >
              Clear
            </button>
            <button
              type="button"
              className="personnel-picker-modal__done-btn"
              onClick={onClose}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
